import * as React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus,
  X,
  ChevronDown,
  ArrowRightLeft,
  Landmark,
  PiggyBank,
  Target,
  BarChart,
  LayoutDashboard,
  RefreshCw,
  ListChecks,
  Bell,
  Puzzle,
  Settings,
  FileText,
  Trash2,
  LayoutGrid,
  Receipt,
  Camera,
} from 'lucide-react';
import { useDraftList } from '@/hooks/useDraftList';
import { useAppSelector } from '@/features/hooks';
import { useFABContext } from '@/components/common/FABContext';

// ── Navigation tree ───────────────────────────────────────────────────────────

interface NavItem {
  href: string;
  label: string;
  icon: React.ElementType;
}

interface NavSection {
  id: string;
  label: string;
  items: NavItem[];
  defaultOpen?: boolean;
}

const NAV_SECTIONS: NavSection[] = [
  {
    id: 'application',
    label: 'Application',
    defaultOpen: true,
    items: [
      { href: '/dashboard',    label: 'Dashboard',    icon: LayoutDashboard },
      { href: '/transactions', label: 'Transactions', icon: ArrowRightLeft  },
      { href: '/accounts',     label: 'Accounts',     icon: Landmark        },
      { href: '/budgets',      label: 'Budgets',      icon: PiggyBank       },
      { href: '/goals',        label: 'Goals',        icon: Target          },
      { href: '/insights',     label: 'Insights',     icon: BarChart        },
    ],
  },
  {
    id: 'advanced',
    label: 'Advanced',
    defaultOpen: false,
    items: [
      { href: '/dashboards',    label: 'Dashboards',    icon: LayoutGrid  },
      { href: '/subscriptions', label: 'Subscriptions', icon: RefreshCw   },
      { href: '/bills',         label: 'Bills',         icon: Receipt     },
      { href: '/receipts',      label: 'Receipts',      icon: Camera      },
      { href: '/rules',         label: 'Rules',         icon: ListChecks  },
      { href: '/notifications', label: 'Notifications', icon: Bell        },
      { href: '/extensions',    label: 'Extensions',    icon: Puzzle      },
    ],
  },
];

const SETTINGS_ITEM: NavItem = { href: '/settings', label: 'Settings', icon: Settings };

// ── Position persistence [T94P2] ──────────────────────────────────────────────

const FAB_POSITION_KEY = 'fintrack:fab-position';
const FAB_SIZE = 56;
const EDGE_MARGIN = 16;
const LONG_PRESS_MS = 500;

// ── Page-action config ────────────────────────────────────────────────────────

interface PageAction {
  label: string;
  icon: React.ElementType;
  onClick: () => void;
  color: string;
}

// ── Main component ────────────────────────────────────────────────────────────

/**
 * [T-FAB] FloatingActionButton — contextual nav + action panel.
 *
 * Opens a floating panel above the button containing:
 *   1. Saved draft chips (amber). Shown only when drafts exist.
 *   2. Page-specific "create" actions. Only shown when the current page has
 *      registered FABContext callbacks.
 *   3. Navigation sections: "Application" (default open) and "Advanced"
 *      (default collapsed), each collapsible with a chevron, plus "Settings".
 */
export function FloatingActionButton() {
  const [open, setOpen] = React.useState(false);
  const [confirmDiscardKey, setConfirmDiscardKey] = React.useState<string | null>(null);

  const [expandedSections, setExpandedSections] = React.useState<Record<string, boolean>>(() =>
    Object.fromEntries(NAV_SECTIONS.map((s) => [s.id, s.defaultOpen ?? false]))
  );

  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;

  const userId = useAppSelector((state) => state.auth.user?.id);
  const { drafts, discard, refresh } = useDraftList(userId);
  const { actions, hideFAB } = useFABContext();

  // ── Draggable positioning [T94P2] ──────────────────────────────────────────
  const [pos, setPos] = React.useState<{ x: number; y: number }>(() => {
    try {
      const stored = localStorage.getItem(FAB_POSITION_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (typeof parsed.x === 'number' && typeof parsed.y === 'number') return parsed;
      }
    } catch { /* ignore */ }
    return { x: -1, y: -1 }; // sentinel → use default
  });
  const [isDragging, setIsDragging] = React.useState(false);
  const longPressTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const dragOriginRef = React.useRef<{ x: number; y: number } | null>(null);
  const hasDraggedRef = React.useRef(false);

  // Compute resolved position (default or clamped to viewport)
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
  const resolvedPos = pos.x < 0 || pos.y < 0
    ? { x: vw - FAB_SIZE - 24, y: 24 }
    : {
        x: Math.max(EDGE_MARGIN, Math.min(pos.x, vw - FAB_SIZE - EDGE_MARGIN)),
        y: Math.max(EDGE_MARGIN, Math.min(pos.y, vh - FAB_SIZE - EDGE_MARGIN)),
      };
  const isOnRight = resolvedPos.x > vw / 2;

  React.useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const close = React.useCallback(() => setOpen(false), []);

  // Navigate to a draft's page and signal it to open the form
  const navigateToDraft = React.useCallback(
    (route: string) => {
      close();
      navigate(route, { state: { openForm: true } });
    },
    [close, navigate]
  );

  const toggleSection = React.useCallback((id: string) => {
    setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const pageActions = React.useMemo<PageAction[]>(() => {
    const items: PageAction[] = [];
    if (actions.onAddTransaction)
      items.push({ label: 'New Transaction', icon: ArrowRightLeft, onClick: () => { actions.onAddTransaction!(); close(); }, color: 'text-blue-600 dark:text-blue-400' });
    if (actions.onAddBudget)
      items.push({ label: 'New Budget', icon: PiggyBank, onClick: () => { actions.onAddBudget!(); close(); }, color: 'text-emerald-600 dark:text-emerald-400' });
    if (actions.onAddGoal)
      items.push({ label: 'New Goal', icon: Target, onClick: () => { actions.onAddGoal!(); close(); }, color: 'text-purple-600 dark:text-purple-400' });
    if (actions.onAddAccount)
      items.push({ label: 'New Account', icon: Landmark, onClick: () => { actions.onAddAccount!(); close(); }, color: 'text-orange-600 dark:text-orange-400' });
    if (actions.onAddSubscription)
      items.push({ label: 'New Subscription', icon: RefreshCw, onClick: () => { actions.onAddSubscription!(); close(); }, color: 'text-teal-600 dark:text-teal-400' });
    if (actions.onAddBill)
      items.push({ label: 'New Bill', icon: FileText, onClick: () => { actions.onAddBill!(); close(); }, color: 'text-rose-600 dark:text-rose-400' });
    return items;
  }, [actions, close]);

  // ── Drag event handlers [T94P2] ────────────────────────────────────────────
  const handleFabPointerDown = React.useCallback((e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragOriginRef.current = { x: e.clientX, y: e.clientY };
    longPressTimerRef.current = setTimeout(() => {
      setIsDragging(true);
      setOpen(false);
      hasDraggedRef.current = true;
    }, LONG_PRESS_MS);
  }, []);

  const handleFabPointerMove = React.useCallback((e: React.PointerEvent) => {
    if (!dragOriginRef.current) return;
    // Cancel long-press if pointer moved significantly before timer fires
    if (!isDragging && longPressTimerRef.current) {
      if (Math.abs(e.clientX - dragOriginRef.current.x) > 8 ||
          Math.abs(e.clientY - dragOriginRef.current.y) > 8) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
      return;
    }
    if (!isDragging) return;
    setPos({
      x: e.clientX - FAB_SIZE / 2,
      y: window.innerHeight - e.clientY - FAB_SIZE / 2,
    });
  }, [isDragging]);

  const handleFabPointerUp = React.useCallback((e: React.PointerEvent) => {
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    if (isDragging) {
      const curVw = window.innerWidth;
      const curVh = window.innerHeight;
      const snapX = e.clientX < curVw / 2 ? EDGE_MARGIN : curVw - FAB_SIZE - EDGE_MARGIN;
      const snapY = Math.max(EDGE_MARGIN, Math.min(
        curVh - e.clientY - FAB_SIZE / 2, curVh - FAB_SIZE - EDGE_MARGIN,
      ));
      const snapped = { x: snapX, y: snapY };
      setPos(snapped);
      try { localStorage.setItem(FAB_POSITION_KEY, JSON.stringify(snapped)); } catch { /* ignore */ }
      setIsDragging(false);
    }
    dragOriginRef.current = null;
  }, [isDragging]);

  const handleFabPointerCancel = React.useCallback(() => {
    if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }
    if (isDragging) setIsDragging(false);
    dragOriginRef.current = null;
  }, [isDragging]);

  const handleFabClick = React.useCallback(() => {
    if (hasDraggedRef.current) { hasDraggedRef.current = false; return; }
    setOpen((prev) => !prev);
  }, []);

  const draftCount = drafts.length;
  const hasDrafts = draftCount > 0;
  const hasActions = pageActions.length > 0;

  if (hideFAB) return null;

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40"
          onClick={close}
          aria-hidden="true"
          data-testid="fab-backdrop"
        />
      )}

      {/* FAB root [T94P2] — draggable with position persistence */}
      <div
        className={cn(
          'fixed z-50',
          !open && !isDragging && 'pointer-events-none'
        )}
        style={{
          left: resolvedPos.x,
          bottom: resolvedPos.y,
          transition: isDragging ? 'none' : 'left 300ms ease, bottom 300ms ease',
        }}
        data-testid="fab-container"
      >
        {/* ── Navigation panel — absolute above FAB [T94P2] ─────────── */}
        <div
          className={cn(
            'absolute bottom-full mb-2 w-56 rounded-2xl border bg-background/95 shadow-2xl backdrop-blur-[2px]',
            'overflow-hidden transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]',
            isOnRight ? 'right-0 origin-bottom-right' : 'left-0 origin-bottom-left',
            open
              ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto'
              : 'opacity-0 scale-95 translate-y-4 pointer-events-none'
          )}
          data-testid="fab-panel"
          aria-hidden={!open}
        >
          {/* DRAFTS */}
          {hasDrafts && (
            <section>
              <div className="px-3 pt-3 pb-1">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Drafts</p>
              </div>
              <ul className="px-2 pb-2 space-y-0.5">
                {drafts.map((draft) => (
                  <li key={draft.key} className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-amber-50 dark:hover:bg-amber-950/40 group">
                    <FileText className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                    <button
                      type="button"
                      onClick={() => navigateToDraft(draft.route)}
                      className="flex-1 truncate text-left text-sm text-amber-700 dark:text-amber-300 hover:underline cursor-pointer"
                      title={`${draft.label} (${draft.ageLabel})`}
                      data-testid={`fab-draft-open-${draft.formId}`}
                    >
                      {draft.label}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setConfirmDiscardKey(draft.key); }}
                      className="rounded p-0.5 text-muted-foreground hover:text-destructive opacity-60 hover:opacity-100 transition-opacity"
                      aria-label={`Discard ${draft.label}`}
                      data-testid={`fab-draft-discard-${draft.formId}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </li>
                ))}
              </ul>
              <div className="mx-3 border-t" />
            </section>
          )}

          {/* PAGE ACTIONS */}
          {hasActions && (
            <section>
              <div className="px-3 pt-3 pb-1">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Actions</p>
              </div>
              <ul className="px-2 pb-2 space-y-0.5">
                {pageActions.map((action) => (
                  <li key={action.label}>
                    <button
                      type="button"
                      onClick={action.onClick}
                      className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
                      data-testid={`fab-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <action.icon className={cn('h-4 w-4 shrink-0', action.color)} />
                      <span>{action.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
              <div className="mx-3 border-t" />
            </section>
          )}

          {/* NAV SECTIONS */}
          <nav className="py-2" aria-label="FAB navigation">
            {NAV_SECTIONS.map((section) => {
              const isExpanded = expandedSections[section.id] ?? false;
              return (
                <div key={section.id}>
                  <button
                    type="button"
                    onClick={() => toggleSection(section.id)}
                    className="flex w-full items-center justify-between px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                    aria-expanded={isExpanded}
                    data-testid={`fab-section-${section.id}`}
                  >
                    <span>{section.label}</span>
                    <ChevronDown className={cn('h-3 w-3 transition-transform duration-200', isExpanded && 'rotate-180')} />
                  </button>

                  <div
                    className="transition-opacity duration-200 ease-in-out"
                    hidden={!isExpanded}
                  >

                    <ul className="px-2 pb-1 space-y-0.5">
                      {section.items.map((item) => {
                        const isActive = pathname.startsWith(item.href);
                        return (
                          <li key={item.href}>
                            <Link
                              to={item.href}
                              onClick={close}
                              className={cn(
                                'flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm transition-colors',
                                isActive
                                  ? 'bg-primary/10 text-primary font-medium'
                                  : 'text-foreground/70 hover:bg-accent hover:text-accent-foreground'
                              )}
                              data-testid={`fab-nav-${item.href.slice(1)}`}
                            >
                              <item.icon className="h-4 w-4 shrink-0" />
                              <span>{item.label}</span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              );
            })}

            {/* Settings */}
            <div className="mx-3 border-t mt-1 mb-1.5" />
            <div className="px-2">
              <Link
                to={SETTINGS_ITEM.href}
                onClick={close}
                className={cn(
                  'flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm transition-colors',
                  pathname.startsWith(SETTINGS_ITEM.href)
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-foreground/70 hover:bg-accent hover:text-accent-foreground'
                )}
                data-testid="fab-nav-settings"
              >
                <SETTINGS_ITEM.icon className="h-4 w-4 shrink-0" />
                <span>{SETTINGS_ITEM.label}</span>
              </Link>
            </div>
          </nav>
        </div>

        {/* ── FAB toggle button [T94P2] — long-press to drag ────────── */}
        <Button
          size="icon"
          className={cn(
            'h-14 w-14 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 relative shrink-0 pointer-events-auto touch-none',
            isDragging && 'scale-110 shadow-2xl ring-2 ring-primary/50 animate-pulse'
          )}
          onClick={handleFabClick}
          onPointerDown={handleFabPointerDown}
          onPointerMove={handleFabPointerMove}
          onPointerUp={handleFabPointerUp}
          onPointerCancel={handleFabPointerCancel}
          data-testid="fab-toggle"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          aria-haspopup="true"
        >
          <Plus
            className={cn(
              'h-6 w-6 absolute transition-all duration-300',
              open ? 'rotate-90 opacity-0 scale-75' : 'rotate-0 opacity-100 scale-100'
            )}
          />
          <X
            className={cn(
              'h-6 w-6 absolute transition-all duration-300',
              open ? 'rotate-0 opacity-100 scale-100' : '-rotate-90 opacity-0 scale-75'
            )}
          />
          {hasDrafts && !open && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs pointer-events-none"
            >
              {draftCount > 9 ? '9+' : draftCount}
            </Badge>
          )}
        </Button>

      </div>

      {/* Draft delete confirmation dialog */}
      <AlertDialog
        open={confirmDiscardKey !== null}
        onOpenChange={(v) => !v && setConfirmDiscardKey(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this draft?</AlertDialogTitle>
            <AlertDialogDescription>
              This draft will be permanently deleted. Any unsaved progress will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmDiscardKey(null)}>Keep Draft</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmDiscardKey) discard(confirmDiscardKey);
                setConfirmDiscardKey(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default FloatingActionButton;
